import type { TableDateFormat } from '../date';
import type { Security } from '../pathLookup';
import type { NamedCellContentRenderFunction } from '../table/columns/TableColumnConfig';
import type {
  EnumColumnOptionKeyTypes,
  EnumColumnOptions,
} from '../table/columns/types/enumColumn';
import type { BooleanSelectFilterOption } from '../table/filters/types/booleanFilter';
import { makeTypeGuardByPropertyValue } from '../types/makeTypeGuardByPropertyValue';
import type { StringTemplate } from '../utils/StringTemplate';
import type { OasTableColumnFilter } from './OasTableColumnFilter';
import type { OasTableColumnLink } from './OasTableColumnLink';

/**
 * OAS definition transformed to TableColumnConfig common for all columns
 *
 * @see TableColumnConfig
 */
export type OasTableColumnBase = {
  key: string;
  title?: string;
  description?: string;
  deprecated?: boolean;
  width?: number;
  fixedWidth?: boolean;
  filter?: OasTableColumnFilter | false;
  array?: boolean;
  /** Hidden in UI by default */
  hidden?: boolean;
  /** Do not allow displaying this column in UI */
  disabledUI?: boolean;
  disabledSort?: boolean;
  /** Do not allow selecting this column as the colorized column of a report */
  disabledColorize?: boolean;
  renderContent?: NamedCellContentRenderFunction;
  /** Identifies whether the column is serial number of a device **/
  isDeviceSn?: boolean;
};

export type OasTableColumnWithLink = {
  link?: OasTableColumnLink;
};

export type OasTableColumnText = {
  type: 'text';
  multiline?: boolean;
} & OasTableColumnBase &
  OasTableColumnWithLink;

export type OasTableColumnNumber = {
  type: 'number';
  decimals?: number;
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnText = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnText
>()('type', 'text');

export const isOasTableColumnNumber = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnNumber
>()('type', 'number');

export type OasTableColumnHostname = {
  type: 'hostname';
  snField: string;
  array?: false;
} & OasTableColumnBase;

export const isOasTableColumnHostname = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnHostname
>()('type', 'hostname');

export type OasTableColumnNtpSummarySources = {
  type: 'ntpSummarySources';
  toWebString: StringTemplate;
  toWebStringTemplateFallback?: string | Record<string, unknown>;
} & OasTableColumnBase;

export const isOasTableColumnNtpSummarySources = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnNtpSummarySources
>()('type', 'ntpSummarySources');

export type OasTableColumnHostnameObject = {
  type: 'hostnameObject';
  hostnameProp: string;
  snProp: string;
} & OasTableColumnBase;

export const isOasTableColumnHostnameObject = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnHostnameObject
>()('type', 'hostnameObject');

export type OasTableColumnObject = {
  type: 'object';
  toWebString: StringTemplate | StringTemplate[];
  toWebStringTemplateFallback?: string | Record<string, unknown>;
  toWebStringTemplateDelimiter?: string;
} & OasTableColumnBase;

export const isOasTableColumnObject = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnObject
>()('type', 'object');

export type OasTableColumnBoolean = {
  type: 'boolean';
  options?: BooleanSelectFilterOption[];
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnBoolean = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnBoolean
>()('type', 'boolean');

export type OasTableColumnUptime = {
  type: 'uptime';
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnUptime = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnUptime
>()('type', 'uptime');

export type OasTableColumnDateTime = {
  type: 'dateTime';
  array?: false;
  format: TableDateFormat;
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnDateTime = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnDateTime
>()('type', 'dateTime');

export type OasTableColumnEnum = {
  type: 'enum';
  multiSelectFilter?: boolean;
  options: EnumColumnOptions;
  optionKeyType?: EnumColumnOptionKeyTypes;
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnEnum = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnEnum
>()('type', 'enum');

export type OasTableColumnCompactNumber = {
  type: 'compactNumber';
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnCompactNumber = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnCompactNumber
>()('type', 'compactNumber');

export type OasTableColumnAsn = {
  type: 'asn';
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnAsn = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnAsn
>()('type', 'asn');

export type OasTableColumnUrl = {
  type: 'url';
  label?: string;
} & OasTableColumnBase;

export const isOasTableColumnUrl = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnUrl
>()('type', 'url');

export type OasTableColumnMplsNextHop = {
  type: 'mplsNextHop';
  label?: string;
} & OasTableColumnBase;

export const isOasTableColumnMplsNextHop = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnMplsNextHop
>()('type', 'mplsNextHop');

export type OasTableColumnSecurity = {
  type: 'security';
  hostSnField: string;
  ruleNameField: string;
  highlightedRuleIndexField?: string;
  securityType: Security.SecurityType;
  array?: false;
} & OasTableColumnBase;

export const isOasTableColumnSecurity = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnSecurity
>()('type', 'security');

export type OasTableColumnBgpRouting = {
  type: 'bgpRouting';
  toWebString: StringTemplate;
  toWebStringTemplateFallback?: string | Record<string, unknown>;
  toWebStringTemplateDelimiter?: string;
} & OasTableColumnBase;

export const isOasTableColumnBgpRouting = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnBgpRouting
>()('type', 'bgpRouting');
export type OasTableColumnSwitchportIntName = {
  type: 'switchportIntName';
  macCountField?: string;
} & OasTableColumnBase &
  OasTableColumnWithLink;

export const isOasTableColumnSwitchportIntName = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnSwitchportIntName
>()('type', 'switchportIntName');
export type OasTableColumnNeighborIp = {
  type: 'neighborIp';
  protocolField: string;
  array?: false;
} & OasTableColumnBase;

export const isOasTableColumnNeighborIp = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnNeighborIp
>()('type', 'neighborIp');

export type OasTableColumnInterface = {
  type: 'interface';
  snField: string;
  array?: false;
} & OasTableColumnBase;

export const isOasTableColumnInterface = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnInterface
>()('type', 'interface');

/**
 * Always a string array at runtime. Do not set `array` in OAS YAML —
 * `createLabelsColumn` forces `array: true`.
 */
export type OasTableColumnLabels = {
  type: 'labels';
  targetIdField: string;
  targetType: 'device' | 'intL2';
} & OasTableColumnBase;

export const isOasTableColumnLabels = makeTypeGuardByPropertyValue<
  OasTableColumn,
  OasTableColumnLabels
>()('type', 'labels');

export type OasTableColumn =
  | OasTableColumnText
  | OasTableColumnLabels
  | OasTableColumnNumber
  | OasTableColumnHostname
  | OasTableColumnObject
  | OasTableColumnBoolean
  | OasTableColumnUptime
  | OasTableColumnDateTime
  | OasTableColumnEnum
  | OasTableColumnCompactNumber
  | OasTableColumnAsn
  | OasTableColumnUrl
  | OasTableColumnSecurity
  | OasTableColumnMplsNextHop
  | OasTableColumnBgpRouting
  | OasTableColumnNtpSummarySources
  | OasTableColumnSwitchportIntName
  | OasTableColumnNeighborIp
  | OasTableColumnHostnameObject
  | OasTableColumnInterface;
